'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  Users,
  UserCheck,
  FileText,
  ArrowRight,
  ChevronDown,
  Target,
  MapPin,
  Repeat,
  Sparkles,
  Handshake,
} from 'lucide-react';
import BiharHeader from '../components/BiharHeader';

type Method = {
  id: string;
  icon: React.ReactNode;
  name: string;
  nameHi: string;
  tagline: string;
  taglineHi: string;
  detail: string;
  detailHi: string;
  example: string;
  action: { label: string; labelHi: string; href?: string; scrollTo?: string };
  color: { iconBg: string; hoverBorder: string; chip: string; btn: string };
};

const METHODS: Method[] = [
  {
    id: 'direct',
    icon: <Target className="h-5 w-5 text-white" />,
    name: 'Direct Match',
    nameHi: 'सीधा मैच',
    tagline: 'Exact swap — you both want each other’s post',
    taglineHi: 'आमने-सामने अदला-बदली',
    detail: 'You and another teacher each want the other’s exact post. The cleanest and fastest swap.',
    detailHi: 'आप और कोई दूसरा शिक्षक एक-दूसरे की ठीक वही जगह चाहते हैं। सबसे आसान और तेज़ ट्रांसफर।',
    example: 'Patna ⇄ Darbhanga',
    action: { label: 'Fill form to find', labelHi: 'फॉर्म भरकर खोजें', href: '/bihar/mutual-transfer/form' },
    color: {
      iconBg: 'from-emerald-600 to-emerald-700',
      hoverBorder: 'hover:border-emerald-300',
      chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      btn: 'from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800',
    },
  },
  {
    id: 'district',
    icon: <MapPin className="h-5 w-5 text-white" />,
    name: 'District Match',
    nameHi: 'जिला स्तर मैच',
    tagline: 'Same district, any school or block',
    taglineHi: 'वही जिला, कोई भी स्कूल या ब्लॉक',
    detail: 'No exact post match? Match anyone within your wanted district — block and school need not be the same.',
    detailHi: 'सटीक मैच नहीं मिला? अपने पसंदीदा जिले में किसी से भी मैच करें — ब्लॉक और स्कूल का मेल ज़रूरी नहीं।',
    example: 'Anywhere in Gaya district',
    action: { label: 'See District Matches', labelHi: 'जिला मैच देखें', href: '/dashboard?section=matches&tab=district' },
    color: {
      iconBg: 'from-blue-600 to-blue-700',
      hoverBorder: 'hover:border-blue-300',
      chip: 'bg-blue-50 text-blue-700 border-blue-200',
      btn: 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
    },
  },
  {
    id: 'three-way',
    icon: <Repeat className="h-5 w-5 text-white" />,
    name: '3-Way Match',
    nameHi: 'तीन-तरफा मैच',
    tagline: 'Three teachers rotate A → B → C → A',
    taglineHi: 'तीन शिक्षक चक्र में जगह बदलें',
    detail: 'When no direct pair exists, three teachers move in a circle so everyone reaches their chosen place.',
    detailHi: 'जब सीधी जोड़ी न मिले, तीन शिक्षक चक्र में जगह बदलते हैं ताकि सबको मनचाही जगह मिले।',
    example: 'A → B → C → A',
    action: { label: 'See 3-way matches', labelHi: 'तीन-तरफा मैच देखें', href: '/dashboard?section=matches&tab=chain' },
    color: {
      iconBg: 'from-amber-500 to-amber-600',
      hoverBorder: 'hover:border-amber-300',
      chip: 'bg-amber-50 text-amber-700 border-amber-200',
      btn: 'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700',
    },
  },
  {
    id: 'magic',
    icon: <Sparkles className="h-5 w-5 text-white" />,
    name: 'Magic Match',
    nameHi: 'मैजिक मैच',
    tagline: 'Nearby & adjacent districts',
    taglineHi: 'पास के और सटे हुए जिले',
    detail: 'Can’t find an exact or district match? We suggest swaps in neighbouring districts close to your choice.',
    detailHi: 'सटीक या जिला मैच नहीं मिला? हम आपकी पसंद के पास के जिलों में अदला-बदली सुझाते हैं।',
    example: 'Patna ↔ nearby Nalanda / Vaishali',
    action: { label: 'Try Magic Match', labelHi: 'मैजिक मैच आज़माएं', href: '/dashboard?section=matches&tab=magic' },
    color: {
      iconBg: 'from-purple-600 to-pink-600',
      hoverBorder: 'hover:border-purple-300',
      chip: 'bg-purple-50 text-purple-700 border-purple-200',
      btn: 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700',
    },
  },
];

const STEPS = [
  {
    icon: <UserCheck className="h-5 w-5 text-white" />,
    title: 'Sign up',
    titleHi: 'साइन अप करें',
    desc: 'With your Bihar teacher ID',
    descHi: 'अपनी शिक्षक आईडी से',
  },
  {
    icon: <FileText className="h-5 w-5 text-white" />,
    title: 'Fill the form',
    titleHi: 'फॉर्म भरें',
    desc: 'Current & wanted place',
    descHi: 'वर्तमान और वांछित जगह',
  },
  {
    icon: <Sparkles className="h-5 w-5 text-white" />,
    title: 'Get matched',
    titleHi: 'मैच पाएं',
    desc: 'All 4 ways, automatically',
    descHi: 'चारों तरीकों से, अपने आप',
  },
  {
    icon: <Handshake className="h-5 w-5 text-white" />,
    title: 'Connect & transfer',
    titleHi: 'जुड़ें और ट्रांसफर करें',
    desc: 'Reveal contact, you’re done',
    descHi: 'संपर्क देखें, हो गया',
  },
];

export default function MutualTransferPage() {
  const [openId, setOpenId] = useState<string | null>(null);

  const handleAction = (m: Method) => {
    if (m.action.scrollTo) {
      document.getElementById(m.action.scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-slate-100 to-white overflow-x-hidden">
      {/* Compact Hero */}
      <BiharHeader
        title="Bihar Mutual Transfer"
        hindiTitle="बिहार पारस्परिक स्थानांतरण"
        description="Find your transfer match — 4 simple ways."
        hindiDescription="अपना ट्रांसफर मैच पाएं — 4 आसान तरीके।"
        badgeText="Bihar Mutual Transfer Portal"
        badgeIcon={<Users className="h-3 w-3 sm:h-4 sm:w-4 text-zinc-600 mr-2" />}
        ctaButton={{
          text: 'Get Started',
          hindiText: 'अभी शुरू करें',
          href: '/bihar/mutual-transfer/form',
          icon: <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 mr-3" />,
        }}
      />

      {/* 4 Ways to Match — the core of the page */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 max-w-4xl">
        <div className="text-center mb-5 sm:mb-6">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-zinc-900">
            4 Ways to Find Your Match
          </h2>
          <p className="text-base sm:text-lg font-semibold text-zinc-700">मैच खोजने के 4 तरीके</p>
          <p className="text-xs sm:text-sm text-zinc-500 mt-1">
            Tap a card to learn more · जानने के लिए कार्ड पर टैप करें
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
          {METHODS.map((m) => {
            const open = openId === m.id;
            const Action: React.ElementType = m.action.href ? Link : 'button';
            const actionProps = m.action.href
              ? { href: m.action.href }
              : { type: 'button' as const, onClick: () => handleAction(m) };
            return (
              <div
                key={m.id}
                className={`bg-white rounded-xl border border-zinc-200 shadow-sm transition-all duration-300 ${m.color.hoverBorder} ${open ? 'sm:col-span-2 shadow-md' : 'hover:shadow-md'}`}
              >
                {/* Card face — clickable header */}
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : m.id)}
                  aria-expanded={open}
                  className="w-full text-left px-3 py-2.5 flex items-center gap-2.5"
                >
                  <div className={`shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${m.color.iconBg} flex items-center justify-center shadow-sm`}>
                    {m.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <h3 className="text-sm font-bold text-zinc-900 leading-tight">{m.name}</h3>
                      <span className="text-xs font-semibold text-zinc-500 truncate">{m.nameHi}</span>
                    </div>
                    <p className="text-xs text-zinc-500 leading-snug truncate">{m.tagline} · {m.taglineHi}</p>
                  </div>
                  <ChevronDown
                    className={`shrink-0 h-4 w-4 text-zinc-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Expanded detail */}
                <div
                  className={`grid transition-all duration-300 ease-in-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                >
                  <div className="overflow-hidden">
                    <div className="px-3 pb-3">
                      <div className="border-t border-zinc-100 pt-2.5">
                        <p className="text-[13px] text-zinc-700 leading-relaxed">{m.detail}</p>
                        <p className="text-[13px] text-zinc-600 leading-relaxed mt-0.5">{m.detailHi}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${m.color.chip}`}>
                            {m.example}
                          </span>
                          <Action
                            {...actionProps}
                            className={`group ml-auto inline-flex items-center gap-2 pl-3.5 pr-3 py-1.5 bg-gradient-to-r ${m.color.btn} text-white rounded-lg transition-all duration-300 hover:shadow-md active:scale-95`}
                          >
                            <span className="flex flex-col leading-none">
                              <span className="text-[13px] font-bold">{m.action.label}</span>
                              <span className="text-[10px] font-medium opacity-90 mt-0.5">{m.action.labelHi}</span>
                            </span>
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-300" />
                          </Action>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* How it works — 4 short steps */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 max-w-5xl">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-zinc-900">How it works</h2>
          <p className="text-base sm:text-lg font-semibold text-zinc-700">यह कैसे काम करता है</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {STEPS.map((s, i) => (
            <div
              key={s.title}
              className="relative bg-white rounded-xl border border-zinc-200 shadow-sm p-4 text-center"
            >
              <div className="mx-auto w-10 h-10 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center shadow mb-2">
                {s.icon}
              </div>
              <span className="absolute top-2 right-3 text-xs font-bold text-zinc-300">{i + 1}</span>
              <h3 className="text-sm sm:text-base font-bold text-zinc-900 leading-tight">{s.title}</h3>
              <p className="text-xs sm:text-sm font-semibold text-zinc-600">{s.titleHi}</p>
              <p className="text-xs text-zinc-500 mt-1">{s.desc}</p>
              <p className="text-xs text-zinc-400">{s.descHi}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-zinc-100 to-slate-100 py-10 sm:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-zinc-900">Ready to start?</h2>
          <p className="text-base sm:text-lg font-semibold text-zinc-700 mb-6">तैयार हैं? अभी शुरू करें</p>
          <div className="flex justify-center">
            <Link
              href="/bihar/mutual-transfer/form"
              className="group inline-flex flex-col items-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold text-lg rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              <span className="flex items-center">
                <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                Find Mutual Transfer
                <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
              <span className="text-sm font-medium opacity-90 mt-0.5">म्यूचुअल ट्रांसफर खोजें</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
